<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-8 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.report.customer_title") }} {{ phone }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-4 col-5 text-right">
					<base-button size="md" type="neutral" @click="$router.push({ name: 'PosReportTopSpenders' })">{{ $t("pos.common.back") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<div class="row">
				<div class="col-lg-4">
					<card>
						<h5 class="mb-3">{{ $t("pos.report.customer_profile") }}</h5>
						<dl class="row mb-0 small">
							<dt class="col-5">{{ $t("pos.report.phone") }}</dt><dd class="col-7">{{ phone }}</dd>
							<dt class="col-5">{{ $t("pos.report.name") }}</dt><dd class="col-7">{{ (customer && customer.name) || "—" }}</dd>
							<dt class="col-5">{{ $t("pos.report.address") }}</dt><dd class="col-7">{{ addressText || "—" }}</dd>
							<dt class="col-5">{{ $t("pos.report.bill_count") }}</dt><dd class="col-7">{{ int(summary.bill_count) }}</dd>
							<dt class="col-5">{{ $t("pos.report.total_spent") }}</dt><dd class="col-7"><strong>{{ money(summary.total) }}</strong></dd>
							<dt class="col-5">{{ $t("pos.report.avg_bill") }}</dt><dd class="col-7">{{ money(summary.avg_bill) }}</dd>
						</dl>
					</card>
				</div>
				<div class="col-lg-8">
					<card v-loading="loading">
						<h5 class="mb-3">{{ $t("pos.report.bills_of_customer") }}</h5>
						<div v-if="!bills.length" class="text-muted">{{ $t("pos.report.no_data") }}</div>
						<el-collapse v-else>
							<el-collapse-item v-for="b in bills" :key="b.id" :name="b.id">
								<template slot="title">
									<span class="mr-3">{{ b.bill_no }}</span>
									<span class="mr-3 text-muted">{{ b.created_at }}</span>
									<strong class="mr-3">{{ money(b.total) }}</strong>
									<el-tag v-if="b.status === 9" type="danger" size="mini">{{ $t("pos.bill.voided_tag") }}</el-tag>
								</template>
								<div class="small text-muted mb-2">
									{{ $t("pos.bill.device") }} {{ b.device_code }} · {{ $t("pos.bill.event") }} {{ b.event_name || $t("pos.filter.event_default") }} ·
									{{ $t("pos.bill.seller") }} {{ b.seller_name }} · {{ b.payment_method === "transfer" ? $t("pos.bill.transfer") : $t("pos.bill.cash") }}
								</div>
								<div v-if="b.status === 9" class="small text-danger mb-2">{{ $t("pos.bill.void_reason") }}: {{ b.void_reason }}</div>
								<table class="table table-sm mb-0">
									<tbody>
										<template v-for="(it, i) in b.items">
											<tr :key="'i' + i">
												<td>{{ it.name }}</td>
												<td class="text-right" style="width: 70px">x{{ it.qty }}</td>
												<td class="text-right" style="width: 110px">{{ money(it.line_amount) }}</td>
											</tr>
											<tr v-for="(c, j) in it.components" :key="'c' + i + '-' + j" class="text-muted small">
												<td colspan="3" class="pl-4">- {{ c.name }} x{{ c.qty }}</td>
											</tr>
										</template>
									</tbody>
								</table>
							</el-collapse-item>
						</el-collapse>
					</card>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Collapse, CollapseItem, Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt, posYmd } from "@/utils/pos";
import posPageMixin from "./posPageMixin";
import PosInstallAlert from "./PosInstallAlert.vue";

export default {
	name: "PosReportCustomer",
	mixins: [posPageMixin],
	components: { RouteBreadCrumb, PosInstallAlert, [Collapse.name]: Collapse, [CollapseItem.name]: CollapseItem, [Tag.name]: Tag },
	data() {
		return { customer: null, summary: {}, bills: [] };
	},
	computed: {
		phone() {
			return this.$route.params.phone;
		},
		addressText() {
			const c = this.customer || {};
			return [c.address_line, c.subdistrict, c.district, c.province, c.zipcode].filter(Boolean).join(" ");
		}
	},
	watch: {
		// Kept alive in the taskbar: leaving this page also changes
		// $route.params.phone (to undefined). Only reload while this page is the
		// active route, or it fetches "/undefined" and pops a 404 swal over
		// whatever page the user moved to.
		"$route.params.phone"(val) {
			if (this.$route.name !== "PosReportCustomer" || val === undefined) return;
			this.load();
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		async load() {
			const q = this.$route.query || {};
			const today = posYmd(new Date());
			const p = { from: q.from || today, to: q.to || today, include_test: q.include_test || 0 };
			if (q.event_id !== undefined && q.event_id !== "") p.event_id = q.event_id;
			if (q.device_id) p.device_id = q.device_id;
			this.loading = true;
			try {
				const res = await PosService.PosReportCustomer(this.phone, p);
				const d = (res.data && res.data.Datum) || {};
				this.customer = d.customer || null;
				this.summary = d.summary || {};
				this.bills = d.bills || [];
				this.notInstalled = false;
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>
